import _ from 'lodash'
import moment from 'moment-timezone'
import { useMemo, createContext, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { DEFAULT_BUSINESS_UNIT } from '@OpsWb/constants'
import {
    PRODUCTION_TABS,
    ProductionTopSearchfilterKeys,
    YEAR_FIRST_DATE_FORMAT,
    DATE_FORMAT_YYYY_MM_DD,
    JobStatusTagList,
    JobStatusTagMap,
    SCHEDULING_TYPES_MAPPING,
    DEFAULT_PROD_COUNTRY,
} from '@ProdWb/constants'
import {
    DrawerCheckBoxSourceType,
    ProductionTabsType,
    DrawerUrlFiltersType,
    CreateUrlPropsType,
    SortDropdownOptionsType,
} from '@ProdWb/types'
import { NoteAttachmentType } from '@ProdWb/JobList/types'
import { JOB_FILTERS } from '@ProdWb/JobList/constants'
import { TableFilterType } from '@OpsWb/types'

import {
    ActiveMfgDrisQuery,
    GetMfgDriUsersQuery,
    MfgPartnerForFilterListFragment,
    useActiveJobBoardRegionsQuery,
    useActiveMfgDrisQuery,
    useExceptionFailureActionsQuery,
    useGetSalesDriUsersQuery,
    useGetSchedulingProcessesQuery,
    useGetSchedulingProcessTypesQuery,
    useMfgPartnersListForFilterQuery,
    useExceptionMpCountriesQueryQuery,
    useGetExceptionCreatedByUserListQuery,
    useGetExceptionTypesQuery,
    useGetMfgDriUsersQuery,
    useGetExceptionActionsQueryQuery,
    useGetBusinessUnitsQuery,
    SchedulingTypesWithLiveJobBoard,
    SchedulingTypes,
    useGetSchedulingTypesQuery,
    ProductionCountry,
} from '~/gql/graphql'
import { OPS_PRODUCTION_PATH } from '~/redux-modules/preferences'

type MfgDriUsersType = GetMfgDriUsersQuery['getMfgDriUsers']
export const ProductionFiltersContext = createContext({
    tab: PRODUCTION_TABS.salesOrders as ProductionTabsType,
    [PRODUCTION_TABS.salesOrders]: '',
    [PRODUCTION_TABS.jobRequests]: '',
    [PRODUCTION_TABS.exceptions]: '',
    [PRODUCTION_TABS.cars]: '',
    [PRODUCTION_TABS.jobs]: '',
    [PRODUCTION_TABS.unbundledProducts]: '',
    setSalesOrdersFilters: _.noop,
    setJobRequestFilters: _.noop,
    setexceptionsFilters: _.noop,
    setCarsFilters: _.noop,
    setJobsFilters: _.noop,
    setUnbundledProductFilters: _.noop,
})

export const ProductionDecodeKeyMap = {
    [ProductionTopSearchfilterKeys.MP]: decodeURIComponent,
    [ProductionTopSearchfilterKeys.PROJECT]: decodeURIComponent,
}

export const ProductionEncodeKeyMap = {
    [ProductionTopSearchfilterKeys.MP]: encodeURIComponent,
    [ProductionTopSearchfilterKeys.PROJECT]: encodeURIComponent,
}

export const ExceptionSortMapping: { [x: string]: string } = {
    name_ascend: 'NAME_ASC',
    name_descend: 'NAME_DESC',
    priority_ascend: 'PRIORITY_ASC',
    priority_descend: 'PRIORITY_DESC',
    shipByDate_ascend: 'EARLIEST_SHIP_DATE_ASC',
    shipByDate_descend: 'EARLIEST_SHIP_DATE_DESC',
    deliveryDate_ascend: 'EARLIEST_DELIVERY_DATE_ASC',
    deliveryDate_descend: 'EARLIEST_DELIVERY_DATE_DESC',
    committedDeliveryDate_ascend: 'COMMITTED_DELIVERY_DATE_ASC',
    committedDeliveryDate_descend: 'COMMITTED_DELIVERY_DATE_DESC',
    region_ascend: 'REGION_ASC',
    region_descend: 'REGION_DESC',
    jobRequests_ascend: 'WORK_ORDER_COUNT_ASC',
    jobRequests_descend: 'WORK_ORDER_COUNT_DESC',
    exceptions_ascend: 'EXCEPTION_COUNT_ASC',
    exceptions_descend: 'EXCEPTION_COUNT_DESC',
    project_ascend: 'PROJECT_NAME_ASC',
    project_descend: 'PROJECT_NAME_DESC',
    mfgDri_ascend: 'MFG_DRI_ASC',
    mfgDri_descend: 'MFG_DRI_DESC',
    carCount_ascend: 'CAR_COUNT_ASC',
    carCount_descend: 'CAR_COUNT_DESC',
    jobCount_ascend: 'JOB_COUNT_ASC',
    jobCount_descend: 'JOB_COUNT_DESC',
}

export const determineTab = (tab: ProductionTabsType) => {
    const tabsArray = Object.values(PRODUCTION_TABS)

    if (tabsArray.indexOf(tab) === -1) {
        return tabsArray[0]
    }
    return tab
}

export const useMpCountryData = (activeTab?: string) => {
    const { data } = useExceptionMpCountriesQueryQuery({
        variables: {},
        skip: (activeTab && activeTab !== PRODUCTION_TABS.jobRequests) || false,
    })

    return data?.mpCountries?.map((country: any) => ({
        VALUE: country.id,
        LABEL: country.name,
    }))
}

export const useMfgDriData = (assignedTo: string) => {
    const { data } = useGetMfgDriUsersQuery({
        variables: { assignedTo },
    })

    return data?.getMfgDriUsers
}

export const useMfgDriDataSource = (dataList: MfgDriUsersType | undefined) => {
    return dataList?.map(object => {
        return {
            LABEL: object?.displayName ?? '',
            VALUE: object?.id.toString() ?? '',
        }
    })
}

export const useBusinessUnitsSource = () => {
    const { data } = useGetBusinessUnitsQuery()

    const businessUnits = useMemo(() => {
        return (
            data?.getBusinessUnits &&
            data?.getBusinessUnits.map(businessUnit => {
                return {
                    LABEL: businessUnit,
                    VALUE: businessUnit,
                }
            })
        )
    }, [data?.getBusinessUnits])

    return [
        ...(businessUnits ?? []),
        { LABEL: DEFAULT_BUSINESS_UNIT, VALUE: DEFAULT_BUSINESS_UNIT },
    ]
}

const useExceptionSource = (
    dataList: { id: number; name: string }[] | undefined | null,
) => {
    return useMemo(() => {
        return dataList?.map(object => {
            return {
                VALUE: object.name,
                LABEL: object.name,
            }
        })
    }, [dataList])
}

export const useExceptionType = (
    activeTab?: string,
    isAutoOtEnabled?: boolean,
) => {
    const { data } = useGetExceptionTypesQuery({
        skip:
            (activeTab && activeTab !== PRODUCTION_TABS.exceptions) ||
            isAutoOtEnabled ||
            false,
    })

    return useExceptionSource(data?.exceptionTypes)
}

export const useExceptionActions = (
    activeTab?: string,
    isAutoOtEnabled?: boolean,
) => {
    const { data } = useGetExceptionActionsQueryQuery({
        skip:
            (activeTab && activeTab !== PRODUCTION_TABS.exceptions) ||
            isAutoOtEnabled ||
            false,
    })

    return useExceptionSource(data?.exceptionActions)
}

export const useExceptionFailureActions = (
    activeTab?: string,
    isAutoOtEnabled?: boolean,
) => {
    const { data } = useExceptionFailureActionsQuery({
        skip:
            (activeTab && activeTab !== PRODUCTION_TABS.exceptions) ||
            !isAutoOtEnabled ||
            false,
    })
    const exceptionFailureActions = data?.exceptionFailureActions

    return useExceptionSource(exceptionFailureActions ?? [])
}

export const useCreatedByUserList = (
    activeTab?: string,
    isAutoOtEnabled?: boolean,
) => {
    const { data } = useGetExceptionCreatedByUserListQuery({
        skip:
            (activeTab && activeTab !== PRODUCTION_TABS.exceptions) ||
            isAutoOtEnabled ||
            false,
    })

    return useMemo(() => {
        return data?.getExceptionCreatedByUserList?.map(object => {
            return {
                LABEL: object.displayName,
                VALUE: object.id.toString(),
            }
        })
    }, [data?.getExceptionCreatedByUserList])
}

export const formatDate = (
    date: string,
    withTimeZone = true,
    onlyDate = false,
) => {
    if (withTimeZone) {
        return date
            ? moment(date).tz(moment.tz.guess()).format(YEAR_FIRST_DATE_FORMAT)
            : ''
    } else if (onlyDate) {
        return date ? moment(date).format(DATE_FORMAT_YYYY_MM_DD) : ''
    } else {
        return date ? moment(date).format(YEAR_FIRST_DATE_FORMAT) : ''
    }
}

export const getNameById = (
    value: string | number,
    data?: DrawerCheckBoxSourceType[],
) => {
    return data?.filter(record => record.value === value)[0]?.label || ''
}

export const useSalesDriDataSource = (
    dataList?: ActiveMfgDrisQuery['getActiveMfgDriUsers'],
) => {
    return useMemo(() => {
        return (
            dataList &&
            dataList?.map(object => {
                return {
                    label: object?.displayName || '',
                    value: object?.id.toString() || '',
                }
            })
        )
    }, [dataList])
}

export const useSalesOrderDriDataSource = (
    dataList?: ActiveMfgDrisQuery['getActiveMfgDriUsers'],
) => {
    return useMemo(
        () =>
            dataList?.map(object => ({
                LABEL: object?.displayName ?? '',
                VALUE: object?.id?.toString() ?? '',
            })),
        [dataList],
    )
}

export const useActiveMfgDriData = () => {
    const { data, loading, error } = useActiveMfgDrisQuery()
    const salesOrderDris = data?.getActiveMfgDriUsers

    return { data: useSalesDriDataSource(salesOrderDris), loading, error }
}

export const useActiveSOMfgDriData = () => {
    const { data, loading, error } = useActiveMfgDrisQuery()
    const salesOrderDris = data?.getActiveMfgDriUsers

    return { data: useSalesOrderDriDataSource(salesOrderDris), loading, error }
}

export const getDimension = (depth: number) => {
    return Math.round(depth * 100) / 100
}

export const getEnumKeys = <
    objectsType extends object,
    keysType extends keyof objectsType = keyof objectsType,
>(
    obj: objectsType,
): keysType[] => {
    return Object.keys(obj).filter(key => Number.isNaN(+key)) as keysType[]
}

export const filterAttachmentIds = (attachments: NoteAttachmentType[]) => {
    const attachmentsIds: string[] = []

    attachments?.map(item => {
        if (item.id) {
            attachmentsIds.push(item.id)
        }
    })

    return attachmentsIds
}

export const getJobRequestStatusTag = (status: string, isList?: boolean) =>
    isList ? JobStatusTagList[status] : JobStatusTagMap[status]

export const useDrawerFilters = (
    filters: DrawerUrlFiltersType,
    rangeFilters: string[],
    mappingObject: {
        [filterName: string]: string
    },
) => {
    const updatedFilters = { ...filters }

    rangeFilters.forEach(rangeFilter => {
        const values = updatedFilters[rangeFilter]

        if (values) {
            updatedFilters[`${rangeFilter}Start`] = [values[0]]
            updatedFilters[`${rangeFilter}End`] = [values[1]]
            delete updatedFilters[rangeFilter]
        }
    })

    return useMemo(() => {
        return Object.keys(updatedFilters).reduce(
            (
                acc: {
                    [key: string]: (string | number)[]
                },
                currentValue,
            ) => {
                if (mappingObject[currentValue]) {
                    acc[mappingObject[currentValue]] =
                        updatedFilters[currentValue]
                }

                return acc
            },
            {},
        )
    }, [updatedFilters, mappingObject])
}

const createUrlWithFilters = ({ newFilters }: CreateUrlPropsType) => {
    const filtersUrl: string[] = []

    Object.keys(newFilters).forEach(filter => {
        if (newFilters[filter].length) {
            filtersUrl.push(`${filter}=${newFilters[filter].toString()}`)
        }
    })

    return filtersUrl.length ? `?${filtersUrl.join('&')}` : ''
}

export const useDrawerUrlFilters = (currentTab: ProductionTabsType) => {
    const contextFilters = useContext(ProductionFiltersContext)

    const navigate = useNavigate()
    const search = contextFilters[currentTab]
    const pathname = `${OPS_PRODUCTION_PATH}/${currentTab}`

    const dispatch = ({
        path,
        filters,
    }: {
        path?: string
        filters?: string
    }) => {
        switch (currentTab) {
            case PRODUCTION_TABS.unbundledProducts:
                contextFilters.setUnbundledProductFilters(filters)
                break
            case PRODUCTION_TABS.jobs:
                contextFilters.setJobsFilters(filters)
                break
            default:
                break
        }
        navigate(`${path}${filters}`)
    }

    const memoizedFilters = useMemo(() => {
        const urlFilters = decodeURIComponent(search)

        const memoFilters: DrawerUrlFiltersType = {}

        if (urlFilters) {
            urlFilters
                .slice(1)
                .split('&')
                .forEach(filter => {
                    const urlFiltersArray = filter.split('=')
                    const filterKey = urlFiltersArray[0]
                    const filterValue = urlFiltersArray[1].split(',')

                    memoFilters[filterKey] = filterValue
                })
        }
        return memoFilters
    }, [search])

    const manageFilter = ({ filter }: { filter: TableFilterType }) => {
        if (!filter?.filterValue?.length) {
            const updatedFilters = {
                ...memoizedFilters,
            }

            delete updatedFilters[filter.filterName]
            const filtersUrl = createUrlWithFilters({
                newFilters: updatedFilters,
            })

            return dispatch({
                path: pathname,
                filters: filtersUrl,
            })
        }

        const updatedFilters = {
            ...memoizedFilters,
            [filter.filterName]: filter.filterValue,
        }

        const filtersUrl = createUrlWithFilters({
            newFilters: updatedFilters,
        })

        return dispatch({
            path: pathname,
            filters: filtersUrl,
        })
    }

    const setBatchFilters = (filters: DrawerUrlFiltersType) => {
        if (filters[JOB_FILTERS.PAYOUT]) {
            filters[JOB_FILTERS.PAYOUT] = filters[JOB_FILTERS.PAYOUT].map(
                value => value || '0',
            )
        }

        const filtersUrl = createUrlWithFilters({
            newFilters: filters,
        })

        return dispatch({
            path: pathname,
            filters: filtersUrl,
        })
    }

    return {
        filtersObject: memoizedFilters,
        manageFilter,
        setBatchFilters,
    }
}

export const getAppliedSort = (
    sortOptions: SortDropdownOptionsType,
    sortArray?: (string | number)[],
) => {
    const appliedSort = sortArray?.map(sortFilter => {
        const sortValues = sortFilter.toString().split('+')
        const sortObject = sortOptions.find(
            option => option.name === sortValues[0],
        )

        if (sortObject) {
            return { ...sortObject, order: sortValues[1] }
        }

        throw new Error('Incorrect map for sort.')
    })

    const remainingSort = _.differenceBy(sortOptions, appliedSort || [], 'name')

    return {
        appliedSort: appliedSort || [],
        remainingSort: remainingSort || [],
    }
}

export const useSchedulingProcesses = () => {
    const { data, loading, error } = useGetSchedulingProcessesQuery()

    return {
        data: useMemo(() => {
            return (
                data?.schedulingProcesses &&
                data?.schedulingProcesses?.map(
                    (object: {
                        name: string
                        id: { toString: () => string }
                    }) => {
                        return {
                            label: object.name,
                            value: object.id.toString(),
                        }
                    },
                )
            )
        }, [data?.schedulingProcesses]),
        loading,
        error,
    }
}

export const useSchedulingProcessTypes = () => {
    const { data, loading, error } = useGetSchedulingProcessTypesQuery()

    return {
        data: useMemo(() => {
            return (
                data?.processTypes &&
                data?.processTypes.map(
                    (object: {
                        name: string
                        id: { toString: () => string }
                    }) => {
                        return {
                            label: object.name,
                            value: object.id.toString(),
                        }
                    },
                )
            )
        }, [data?.processTypes]),
        loading,
        error,
    }
}

export const useSalesDriData = (assignedTo: string) => {
    const { data, loading, error } = useGetSalesDriUsersQuery({
        variables: { assignedTo },
    })

    const salesOrderDris = data?.getMfgDriUsers

    return { data: useSalesDriDataSource(salesOrderDris), loading, error }
}

export const useMfgPartnersData = () => {
    const { data, loading, error } = useMfgPartnersListForFilterQuery()

    const mfgPartners = useMemo(() => {
        return (
            data?.mfgPartnersForFilter &&
            data?.mfgPartnersForFilter?.map(
                (object: MfgPartnerForFilterListFragment) => {
                    return {
                        label: object.displayName,
                        value: object.id.toString(),
                    }
                },
            )
        )
    }, [data?.mfgPartnersForFilter])

    return { data: mfgPartners, loading, error }
}

export const useJobBoardRegionData = () => {
    const { data, loading, error } = useActiveJobBoardRegionsQuery()

    return {
        data: useMemo(() => {
            return (
                data?.getActiveJobBoardRegions &&
                data?.getActiveJobBoardRegions.map(
                    (object: { id: number; name: string }) => {
                        return {
                            label: object.name,
                            value: object.id.toString(),
                        }
                    },
                )
            )
        }, [data?.getActiveJobBoardRegions]),
        loading,
        error,
    }
}

export const useBusinessUnitsData = () => {
    const { data, loading, error } = useGetBusinessUnitsQuery()
    const businessUnits = useMemo(() => {
        return (
            data?.getBusinessUnits &&
            data?.getBusinessUnits.map(businessUnit => {
                return {
                    label: businessUnit,
                    value: businessUnit,
                }
            })
        )
    }, [data?.getBusinessUnits])

    return {
        data: [
            ...(businessUnits ?? []),
            {
                label: DEFAULT_BUSINESS_UNIT,
                value: DEFAULT_BUSINESS_UNIT,
            },
        ],
        loading,
        error,
    }
}

export const useQueryFilterValues = () => {
    const process = useSchedulingProcesses()
    const processType = useSchedulingProcessTypes()
    const salesDri = useSalesDriData('SO')
    const mfgDri = useSalesDriData('JOB')
    const mfgPartners = useMfgPartnersData()
    const jobBoardRegions = useJobBoardRegionData()
    const businessUnits = useBusinessUnitsData()

    return {
        data: {
            process: process.data,
            processType: processType.data,
            salesDri: salesDri.data,
            mfgDri: mfgDri.data,
            mfgPartners: mfgPartners.data,
            jobBoardRegions: jobBoardRegions.data,
            businessUnits: businessUnits.data,
        },
        loading:
            process.loading ||
            processType.loading ||
            salesDri.loading ||
            mfgDri.loading ||
            mfgPartners.loading ||
            jobBoardRegions.loading ||
            businessUnits.loading,
        error:
            process.error ||
            processType.error ||
            salesDri.error ||
            mfgDri.error ||
            mfgPartners.error ||
            jobBoardRegions.error ||
            businessUnits.error,
    }
}

export const schedulingTypesTitleMapping = (
    schedulingTypesQueryResult?: SchedulingTypesWithLiveJobBoard,
) => {
    return (
        schedulingTypesQueryResult?.schedulingTypes?.map(type => {
            let key

            if (type === SchedulingTypes.JOB_BOARD) {
                key = schedulingTypesQueryResult.isJobBoardLive
                    ? `${type}_LIVE`
                    : `${type}_NEXT`
            } else {
                key = type
            }
            return type ? SCHEDULING_TYPES_MAPPING[key] : ''
        }) ?? []
    )
}

export const useSchedulingTypesQuery = (productionCountry?: string | null) => {
    const { data, loading, refetch } = useGetSchedulingTypesQuery({
        variables: {
            productionCountry: productionCountry as ProductionCountry,
        },
        skip: !productionCountry || productionCountry === DEFAULT_PROD_COUNTRY,
    })

    return {
        schedulingTypes: useMemo(() => {
            return schedulingTypesTitleMapping(data?.getSchedulingTypes)
        }, [data?.getSchedulingTypes]),
        loading,
        refetch,
    }
}
